/** Node Detail Shell (VISTOERA_PRODUCTION_CANVAS_NODE_DETAIL_SHELL_AND_IMAGE_ASSET_UI_V2 §3/§4/§17).
 *
 * Renders the attached-below detail panel through @xyflow/react's own
 * `NodeToolbar` — audited in `NodeToolbar.js`/`index.mjs` before picking
 * this over a hand-rolled overlay:
 *
 *  - It portals into `.react-flow__renderer` (a sibling of the
 *    transformed `.react-flow__pane`/viewport, not a child of any node's
 *    own DOM), so this panel can NEVER change a node's real `width`/
 *    `height` in the graph model, push other nodes, bend edges, or
 *    affect Scene containment (spec §4's entire "don't break the
 *    graph" list) — it isn't part of the graph geometry at all.
 *  - Its wrapper `transform` is computed from the node's rect *plus* the
 *    live pan/zoom transform but the panel's own CSS pixel size is
 *    NOT re-scaled by `zoom` (spec §17's own words: "this toolbar
 *    doesn't scale with the viewport so content is always visible") —
 *    this is exactly the "text must stay readable even at 30% zoom"
 *    requirement, satisfied by the library itself rather than a custom
 *    counter-scale hack.
 *  - It re-positions on every store update (drag/pan/zoom all funnel
 *    through the same `transform`/`nodeLookup` state `NodeToolbar`
 *    already subscribes to), so "follows the node, never drifts" is
 *    the library's own contract, not something this file re-implements.
 *  - Only ONE `NodeDetailShell` is ever mounted (by `CanvasBoard`, keyed
 *    off its single `expandedNodeId` piece of state) — "only one panel
 *    expanded at a time" (spec §16) falls out of that architecture for
 *    free instead of being a rule every node has to remember to obey.
 *
 * Handles/edges are drawn by the compact node's own `<Handle>` elements,
 * untouched by anything here (spec §18).
 */

import { NodeToolbar, Position } from '@xyflow/react'
import { UpOutlined } from '@ant-design/icons'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  nodeId: string
  title: string
  icon: ReactNode
  color: string
  width?: number
  onCollapse: () => void
  children: ReactNode
}

export default function NodeDetailShell({ nodeId, title, icon, color, width, onCollapse, children }: Props) {
  const { t } = useTranslation('canvas')

  return (
    <NodeToolbar
      nodeId={nodeId}
      isVisible
      position={Position.Bottom}
      offset={10}
      align="start"
      className="pcv-detail-toolbar"
    >
      <div
        className="pcv-detail-panel"
        style={width ? { width } : undefined}
        // Clicks inside the panel (text selection, copy buttons, candidate
        // thumbnails, …) must never bubble into `onPaneClick`'s "blank
        // canvas click collapses the panel" handler (spec §16).
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pcv-detail-panel-header">
          <span className="pcv-detail-panel-icon" style={{ color, background: `${color}22` }}>
            {icon}
          </span>
          <span className="pcv-detail-panel-title" title={title}>
            {title}
          </span>
          <button
            type="button"
            className="pcv-detail-panel-collapse"
            onClick={onCollapse}
            title={t('detail.collapse')}
            aria-label={t('detail.collapse')}
          >
            <UpOutlined />
          </button>
        </div>
        <div className="pcv-detail-panel-body">{children}</div>
      </div>
    </NodeToolbar>
  )
}
